import { getScrollEventTarget } from '@theme/scroll-container';

class SupportFaq extends HTMLElement {
  static #compactNavQuery = '(max-width: 749px)';

  /** @type {AbortController} */
  #controller = new AbortController();

  /** @type {ResizeObserver | null} */
  #toolbarObserver = null;

  /** @type {string | null} */
  #focusedCategoryId = null;

  /** @type {boolean} */
  #navExpandedByUser = false;

  /** @type {number} */
  #expandScrollY = 0;

  /** @type {boolean} */
  #scrollSpyLocked = false;

  /** @type {number} */
  #spyRaf = 0;

  /** @type {ReturnType<typeof setTimeout> | number} */
  #spyLockTimer = 0;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    const search = this.querySelector('[data-support-faq-search]');
    const clear = this.querySelector('[data-support-faq-clear]');
    const nav = this.querySelector('[data-support-faq-nav]');
    const toolbar = this.querySelector('.support-faq__toolbar');
    const compactNav = window.matchMedia(SupportFaq.#compactNavQuery);

    if (search instanceof HTMLInputElement) {
      search.addEventListener('input', () => this.#onSearch(search.value), { signal });
      search.addEventListener(
        'keydown',
        (event) => {
          if (event.key === 'Escape') {
            search.value = '';
            this.#onSearch('');
            search.blur();
          }
        },
        { signal }
      );
    }

    if (clear instanceof HTMLButtonElement) {
      clear.addEventListener(
        'click',
        () => {
          if (search instanceof HTMLInputElement) {
            search.value = '';
            search.focus();
          }
          this.#onSearch('');
        },
        { signal }
      );
    }

    this.#buildNav();

    if (nav) {
      nav.addEventListener('click', this.#onNavClick, { signal });
    }

    this.addEventListener('toggle', this.#onItemToggle, { signal, capture: true });
    window.addEventListener('hashchange', this.#onHashChange, { signal });
    getScrollEventTarget().addEventListener('scroll', this.#onWindowScroll, { signal, passive: true });
    window.addEventListener('scroll', this.#onWindowScroll, { signal, passive: true });
    compactNav.addEventListener('change', this.#onCompactNavChange, { signal });

    if (toolbar instanceof HTMLElement) {
      const syncToolbarHeight = () => {
        this.style.setProperty('--support-faq-toolbar-height', `${toolbar.getBoundingClientRect().height}px`);
        this.#syncStuckToolbar();
      };
      syncToolbarHeight();
      this.#toolbarObserver = new ResizeObserver(syncToolbarHeight);
      this.#toolbarObserver.observe(toolbar);
    }

    this.#applyHash(window.location.hash, { scroll: true });
    this.#ensureSingleOpen();
    this.#syncScrollSpy();
    this.#syncStuckToolbar();
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#toolbarObserver?.disconnect();
    this.#toolbarObserver = null;
    if (this.#spyRaf) cancelAnimationFrame(this.#spyRaf);
    this.#spyRaf = 0;
    window.clearTimeout(this.#spyLockTimer);
  }

  /**
   * Theme block settings are not available when looping `section.blocks`
   * in the parent, so nav labels are taken from the rendered categories.
   */
  #buildNav() {
    const nav = this.querySelector('[data-support-faq-nav]');
    if (!(nav instanceof HTMLElement)) return;

    nav.replaceChildren();

    this.querySelectorAll('[data-support-faq-category]').forEach((category) => {
      if (!(category instanceof HTMLElement) || !category.id) return;

      const title =
        category.dataset.navLabel ||
        category.querySelector('.support-faq-category__title')?.textContent?.trim() ||
        '';
      if (!title) return;

      const link = document.createElement('a');
      link.className = 'support-faq__nav-link';
      link.href = `#${category.id}`;
      link.dataset.navFor = category.id;
      link.textContent = title;
      nav.append(link);
    });
  }

  /**
   * @param {string} rawQuery
   */
  #onSearch(rawQuery) {
    const query = this.#normalize(rawQuery);
    const clear = this.querySelector('[data-support-faq-clear]');
    const empty = this.querySelector('[data-support-faq-empty]');
    const live = this.querySelector('[data-support-faq-live]');
    const items = this.querySelectorAll('[data-support-faq-item]');
    const categories = this.querySelectorAll('[data-support-faq-category]');
    const emptyMessage = this.dataset.emptyMessage || '該当する質問が見つかりませんでした';
    const isSearching = query.length > 0;

    if (clear instanceof HTMLButtonElement) {
      clear.hidden = !isSearching;
    }

    if (isSearching) {
      this.#focusedCategoryId = null;
      this.#navExpandedByUser = false;
    }

    let visibleCount = 0;

    items.forEach((item) => {
      if (!(item instanceof HTMLElement)) return;
      const haystack = this.#normalize(item.dataset.searchText || item.textContent || '');
      const match = !isSearching || haystack.includes(query);
      item.hidden = !match;
      if (match) visibleCount += 1;
    });

    categories.forEach((category) => {
      if (!(category instanceof HTMLElement)) return;
      const hasVisible = Boolean(category.querySelector('[data-support-faq-item]:not([hidden])'));
      category.hidden = !hasVisible;
    });

    this.#syncNavVisibility();

    if (empty instanceof HTMLElement) {
      empty.hidden = visibleCount > 0;
    }

    if (live) {
      live.textContent =
        query.length === 0
          ? ''
          : visibleCount > 0
            ? `${visibleCount}件の質問が見つかりました`
            : emptyMessage;
    }
  }

  /**
   * @param {Event} event
   */
  #onNavClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const link = target.closest('a[data-nav-for]');
    if (!(link instanceof HTMLAnchorElement)) return;

    const id = link.getAttribute('href')?.replace(/^#/, '');
    if (!id) return;

    const destination = this.querySelector(`#${CSS.escape(id)}`);
    if (!(destination instanceof HTMLElement)) return;

    event.preventDefault();

    if (this.#isCompactNav() && this.#focusedCategoryId === id) {
      this.#focusedCategoryId = null;
      this.#navExpandedByUser = true;
      this.#expandScrollY = window.scrollY;
      this.#syncNavVisibility();
      return;
    }

    this.#setActiveLink(id);
    this.#navExpandedByUser = false;
    if (this.#isCompactNav()) {
      this.#focusedCategoryId = id;
      this.#syncNavVisibility();
    }

    history.replaceState(null, '', `#${id}`);
    this.#scrollToTarget(destination);
  };

  /**
   * Keep the URL hash in sync when a question is opened, so answers can be shared.
   * @param {Event} event
   */
  #onItemToggle = (event) => {
    const item = event.target;
    if (!(item instanceof HTMLDetailsElement) || !item.hasAttribute('data-support-faq-item')) return;
    if (!item.open) return;

    this.#closeOtherItems(item);
    if (item.id) history.replaceState(null, '', `#${item.id}`);
    if (this.#isCompactNav()) {
      this.#scrollBelowToolbar(item, { force: true });
    }
  };

  #onHashChange = () => {
    this.#applyHash(window.location.hash, { scroll: true });
  };

  /**
   * @param {string} hash
   * @param {{ scroll?: boolean }} [options]
   */
  #applyHash(hash, options = {}) {
    const id = hash.replace(/^#/, '');
    if (!id) return;

    const target = this.querySelector(`#${CSS.escape(id)}`);
    if (!(target instanceof HTMLElement)) return;

    const item = target.closest('[data-support-faq-item]');
    if (item instanceof HTMLDetailsElement) {
      item.open = true;
      this.#closeOtherItems(item);
    }

    const category = target.closest('[data-support-faq-category]');
    const categoryId =
      target.dataset.categoryId ||
      (category instanceof HTMLElement ? category.dataset.categoryId : '');
    if (categoryId) {
      this.#setActiveLink(categoryId);
      this.#navExpandedByUser = false;
      if (this.#isCompactNav()) {
        this.#focusedCategoryId = categoryId;
        this.#syncNavVisibility();
      }
    }

    if (options.scroll) {
      this.#scrollToTarget(target);
    }
  }

  /**
   * Only one question may be expanded at a time.
   * @param {HTMLDetailsElement} keep
   */
  #closeOtherItems(keep) {
    this.querySelectorAll('[data-support-faq-item]').forEach((item) => {
      if (item instanceof HTMLDetailsElement && item !== keep) {
        item.open = false;
      }
    });
  }

  /**
   * On first paint, collapse extras if several rows were marked open by default.
   */
  #ensureSingleOpen() {
    const opened = [...this.querySelectorAll('[data-support-faq-item][open]')].filter(
      (item) => item instanceof HTMLDetailsElement
    );
    if (opened.length <= 1) return;
    this.#closeOtherItems(/** @type {HTMLDetailsElement} */ (opened[0]));
  }

  /**
   * @param {string} categoryId
   */
  #setActiveLink(categoryId) {
    this.querySelectorAll('[data-nav-for]').forEach((link) => {
      const active = link.getAttribute('data-nav-for') === categoryId;
      link.classList.toggle('is-active', active);
      if (active) {
        link.setAttribute('aria-current', 'true');
      } else {
        link.removeAttribute('aria-current');
      }
    });
  }

  #isCompactNav() {
    return window.matchMedia(SupportFaq.#compactNavQuery).matches;
  }

  /**
   * @param {Element} element
   */
  #scrollToTarget(element) {
    if (!(element instanceof HTMLElement)) return;

    if (this.#isCompactNav()) {
      this.#scrollBelowToolbar(element, { force: true });
      return;
    }

    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /**
   * Place the target just below the sticky search / category toolbar.
   *
   * @param {Element} element
   * @param {{ force?: boolean }} [options]
   */
  #scrollBelowToolbar(element, options = {}) {
    if (!(element instanceof HTMLElement)) return;

    const toolbar = this.querySelector('.support-faq__toolbar');
    const gap = 12;
    const force = Boolean(options.force);

    const align = () => {
      const stickyBottom =
        toolbar instanceof HTMLElement ? toolbar.getBoundingClientRect().bottom : 0;
      const itemTop = element.getBoundingClientRect().top;
      if (!force && itemTop >= stickyBottom + gap) return;

      const top = window.scrollY + itemTop - stickyBottom - gap;
      window.scrollTo({ top: Math.max(0, top), behavior: force ? 'auto' : 'smooth' });
    };

    if (force) this.#lockScrollSpy();

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        align();
        if (force) window.setTimeout(align, 80);
      });
    });
  }

  #onWindowScroll = () => {
    if (this.#spyRaf) return;
    this.#spyRaf = requestAnimationFrame(() => {
      this.#spyRaf = 0;
      this.#syncStuckToolbar();
      this.#syncScrollSpy();
    });
  };

  #onCompactNavChange = () => {
    this.#syncNavVisibility();
    this.#syncStuckToolbar();
    this.#syncScrollSpy();
  };

  #syncStuckToolbar() {
    const toolbar = this.querySelector('.support-faq__toolbar');
    if (!(toolbar instanceof HTMLElement)) return;
    if (!this.classList.contains('support-faq--sticky-nav')) {
      toolbar.classList.remove('is-stuck');
      return;
    }

    const stickyTop = Number.parseFloat(getComputedStyle(toolbar).top) || 0;
    toolbar.classList.toggle('is-stuck', toolbar.getBoundingClientRect().top <= stickyTop + 1);
  }

  #lockScrollSpy() {
    this.#scrollSpyLocked = true;
    window.clearTimeout(this.#spyLockTimer);
    this.#spyLockTimer = window.setTimeout(() => {
      this.#scrollSpyLocked = false;
      this.#syncScrollSpy();
    }, 500);
  }

  #readingLine() {
    const toolbar = this.querySelector('.support-faq__toolbar');
    if (toolbar instanceof HTMLElement) {
      return toolbar.getBoundingClientRect().bottom + 12;
    }
    return 12;
  }

  #categoryIdAtReadingLine() {
    const line = this.#readingLine();
    let current = '';

    this.querySelectorAll('[data-support-faq-category]').forEach((category) => {
      if (!(category instanceof HTMLElement) || category.hidden) return;
      if (category.getBoundingClientRect().top <= line) {
        current = category.dataset.categoryId || category.id || '';
      }
    });

    return current;
  }

  #hasEnteredFaqList() {
    const first = [...this.querySelectorAll('[data-support-faq-category]')].find(
      (category) => category instanceof HTMLElement && !category.hidden
    );
    return first instanceof HTMLElement && first.getBoundingClientRect().top <= this.#readingLine();
  }

  #syncScrollSpy() {
    if (!this.#isCompactNav() || this.#scrollSpyLocked) return;

    const search = this.querySelector('[data-support-faq-search]');
    const isSearching =
      search instanceof HTMLInputElement && this.#normalize(search.value).length > 0;
    if (isSearching) return;

    if (this.#navExpandedByUser) {
      if (Math.abs(window.scrollY - this.#expandScrollY) < 24) return;
      this.#navExpandedByUser = false;
    }

    if (!this.#hasEnteredFaqList()) {
      if (this.#focusedCategoryId !== null) {
        this.#focusedCategoryId = null;
        this.#setActiveLink('');
        this.#syncNavVisibility();
      }
      return;
    }

    const id = this.#categoryIdAtReadingLine();
    if (!id || id === this.#focusedCategoryId) return;

    this.#focusedCategoryId = id;
    this.#setActiveLink(id);
    this.#syncNavVisibility();
  }

  #syncNavVisibility() {
    const search = this.querySelector('[data-support-faq-search]');
    const nav = this.querySelector('[data-support-faq-nav]');
    const isSearching =
      search instanceof HTMLInputElement && this.#normalize(search.value).length > 0;
    const focusedId = this.#isCompactNav() ? this.#focusedCategoryId : null;

    if (nav instanceof HTMLElement) {
      nav.hidden = isSearching;
    }

    this.classList.toggle('support-faq--nav-focused', Boolean(focusedId) && !isSearching);

    this.querySelectorAll('[data-nav-for]').forEach((link) => {
      if (!(link instanceof HTMLElement)) return;

      const id = link.getAttribute('data-nav-for');
      const category = id
        ? this.querySelector(`[data-support-faq-category][data-category-id="${CSS.escape(id)}"]`)
        : null;
      const hasVisible = category instanceof HTMLElement && !category.hidden;
      const collapsedAway = Boolean(focusedId) && id !== focusedId;
      link.hidden = isSearching || !hasVisible || collapsedAway;

      if (focusedId && id === focusedId && !isSearching) {
        link.setAttribute('aria-expanded', 'false');
        const label = link.textContent?.trim();
        if (label) link.setAttribute('aria-label', `${label}、カテゴリ一覧を表示`);
      } else {
        link.removeAttribute('aria-expanded');
        link.removeAttribute('aria-label');
      }
    });

    const toolbar = this.querySelector('.support-faq__toolbar');
    if (toolbar instanceof HTMLElement) {
      this.style.setProperty(
        '--support-faq-toolbar-height',
        `${toolbar.getBoundingClientRect().height}px`
      );
    }

    this.#syncStuckToolbar();
  }

  /**
   * @param {string} value
   * @returns {string}
   */
  #normalize(value) {
    return value.normalize('NFKC').toLowerCase().replace(/\s+/g, '');
  }
}

if (!customElements.get('support-faq')) {
  customElements.define('support-faq', SupportFaq);
}
